import React from "react";
import Cropper, { Area, Point } from "react-easy-crop";
import ReactSlider from "react-slider";
import Size from "../../types/system/Size";
import TTSDebouncer from "../../foundation/TTSDebouncer";

import "./Cropping.scss";

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); 
    image.src = url;
  });

function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width: number, height: number, rotation: number): Size {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

async function getCroppedImg(
    imageSrc: string,
    pixelCrop: Area,
    rotation: number = 0,
    flip = { horizontal: false, vertical: false }
): Promise<string> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx)
        return null;

    const rotRad = getRadianAngle(rotation);

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
        image.width,
        image.height,
        rotation
    );

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    ctx.drawImage(image, 0, 0);

    const data = ctx.getImageData(
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height
    );


    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(data, 0, 0);

    return new Promise((resolve, reject) => {
        canvas.toBlob((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result.toString().replace("data:image/png;base64,", ""));
            }
            reader.readAsDataURL(file);
        }, "image/png");
    });
}

interface CroppingProps {
    url: string,
    open: boolean,
    square?: boolean,
    imageCropStarted?: () => void,
    imageCropped?: (image: string) => void
};

interface CroppingState {
    open: boolean,
    crop: Point,
    zoom: number,
    zoomIndex: number,
    processDebounce: TTSDebouncer
};

class Cropping extends React.Component<CroppingProps, CroppingState> {

    state = {
        open: false,
        crop: { x: 0, y: 0 },
        zoom: 1,
        zoomIndex: 0,
        processDebounce: new TTSDebouncer(500)
    } as CroppingState;

    zoomChanged(index: number) {
        this.setState(() => ({
            zoom: Math.max(1, (index / 100 * 3) + 1),
            zoomIndex: index
        }));
    }
    onCropChange(crop: Point) {
        this.setState(() => ({ crop: crop }));
    }
    onCropComplete(croppedArea: Area) {
        this.props.imageCropStarted();
        if (this.props?.open &&
            this.props?.url) {
            this.state.processDebounce.call(async () => {
                let image = await getCroppedImg(this.props.url, croppedArea);
                this.props?.imageCropped(image);
            });
        }
    }
    
    render() {

        if (!this.state?.open &&
            this.props?.open)
            setTimeout(() => this.setState(() => ({ open: true, zoom: 1, zoomIndex: 0 })), 200);
        else if (!this.props?.open &&
            this.state?.open)
            setTimeout(() => this.setState(() => ({ open: false })), 200);

        return (
        this.state?.open &&
        <div className="cropping-frame">

            <div className={`crop-container${this.props.square ? " square" : ""}`}>
                <Cropper
                    image={this.props.url}
                    crop={this.state?.crop ?? { x: 0, y: 0 }}
                    zoom={this.state?.zoom}
                    showGrid={false}
                    aspect={4 / 3}
                    cropShape={this.props.square ? "rect" : "round"}
                    cropSize={{ width: 200, height: 200 }}
                    onCropChange={crop => this.onCropChange(crop)}
                    onCropComplete={(_, croppedAreaPixels) => this.onCropComplete(croppedAreaPixels)}
                />
            </div>

            <ReactSlider
                className="cropping-slider"
                thumbClassName="thumb"
                trackClassName="track"
                value={this.state.zoomIndex}
                onChange={(index) => this.zoomChanged(index)} />
        </div>
        );

    }

}

export default Cropping;